"use client";

import { useMemo, useState } from "react";
import type { Locale } from "@/i18n-config";
import { cn } from "@/utils/cn";
import type { HelpContactPageCopy } from "@/content/help-center-copy";
import {
  SUPPORT_SCREENSHOT_MAX_BYTES,
  SUPPORT_TOPIC_VALUES,
  type SupportTopic,
  isAllowedSupportScreenshotMimeType,
} from "@/lib/support/constants";

type SupportContactFormProps = {
  lang: Locale;
  copy: HelpContactPageCopy;
  isAuthenticated: boolean;
  initialEmail: string;
};

type FormState = {
  email: string;
  topic: SupportTopic;
  subject: string;
  description: string;
  screenshot: File | null;
};

type FormField = keyof FormState;
type FieldErrors = Partial<Record<FormField | "form", string>>;
type TouchedState = Record<FormField, boolean>;

function isEmailValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function validateForm(args: {
  value: FormState;
  isAuthenticated: boolean;
  copy: HelpContactPageCopy;
}): FieldErrors {
  const { value, isAuthenticated, copy } = args;
  const errors: FieldErrors = {};

  const normalizedEmail = value.email.trim();
  const normalizedSubject = value.subject.trim();
  const normalizedDescription = value.description.trim();

  if (!isAuthenticated && normalizedEmail.length === 0) {
    errors.email = copy.errors.emailRequired;
  } else if (normalizedEmail.length > 0 && !isEmailValid(normalizedEmail)) {
    errors.email = copy.errors.emailInvalid;
  }

  if (!SUPPORT_TOPIC_VALUES.includes(value.topic)) {
    errors.topic = copy.errors.topicRequired;
  }

  if (normalizedSubject.length < 3) {
    errors.subject = copy.errors.subjectRequired;
  }

  if (normalizedDescription.length < 10) {
    errors.description = copy.errors.descriptionRequired;
  }

  if (value.screenshot) {
    if (!isAllowedSupportScreenshotMimeType(value.screenshot.type)) {
      errors.screenshot = copy.errors.screenshotInvalidType;
    } else if (value.screenshot.size > SUPPORT_SCREENSHOT_MAX_BYTES) {
      errors.screenshot = copy.errors.screenshotTooLarge;
    }
  }

  return errors;
}

export function SupportContactForm(props: SupportContactFormProps) {
  const { lang, copy, isAuthenticated, initialEmail } = props;

  const [formValue, setFormValue] = useState<FormState>({
    email: initialEmail,
    topic: SUPPORT_TOPIC_VALUES[0],
    subject: "",
    description: "",
    screenshot: null,
  });
  const [touched, setTouched] = useState<TouchedState>({
    email: false,
    topic: false,
    subject: false,
    description: false,
    screenshot: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const errors = useMemo(
    () =>
      validateForm({
        value: formValue,
        isAuthenticated,
        copy,
      }),
    [copy, formValue, isAuthenticated]
  );

  const shouldShowError = (field: FormField) => showAllErrors || touched[field];
  const isFormValid = Object.keys(errors).length === 0;
  const isSubmitDisabled = isSubmitting || !isFormValid;

  const updateField = <K extends FormField>(field: K, nextValue: FormState[K]) => {
    setFormValue((current) => ({
      ...current,
      [field]: nextValue,
    }));
  };

  const markTouched = (field: FormField) => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowAllErrors(true);
    setSubmitError(null);

    if (!isFormValid) return;

    setIsSubmitting(true);

    const payload = new FormData();
    payload.set("language", lang);
    payload.set("email", formValue.email.trim());
    payload.set("topic", formValue.topic);
    payload.set("subject", formValue.subject.trim());
    payload.set("description", formValue.description.trim());
    if (formValue.screenshot) {
      payload.set("screenshot", formValue.screenshot);
    }

    try {
      const response = await fetch("/api/support/requests", {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        setSubmitError(body?.error ?? copy.errors.submitFailed);
        setIsSubmitting(false);
        return;
      }

      setIsSuccess(true);
      setIsSubmitting(false);
    } catch {
      setSubmitError(copy.errors.submitFailed);
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="rounded-2xl border border-[#d8cab2] bg-[#fffaf1] px-5 py-5 text-sm text-[#32453f]">{copy.successMessage}</div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label htmlFor="support-email" className="text-sm font-medium text-[#2d3f3a]">
          {copy.emailLabel}
        </label>
        <input
          id="support-email"
          type="email"
          value={formValue.email}
          onChange={(event) => updateField("email", event.target.value)}
          onBlur={() => markTouched("email")}
          required={!isAuthenticated}
          autoComplete="email"
          className={cn(
            "mt-2 min-h-11 w-full rounded-xl border bg-[#fffaf1] px-3.5 text-sm text-[#243632] outline-none transition-colors",
            shouldShowError("email") && errors.email ? "border-[#cda98c]" : "border-[#d7cab1] focus:border-[#0f766e]"
          )}
        />
        {shouldShowError("email") && errors.email ? <p className="mt-1 text-xs text-[#8a5c47]">{errors.email}</p> : null}
      </div>

      <div>
        <label htmlFor="support-topic" className="text-sm font-medium text-[#2d3f3a]">
          {copy.topicLabel}
        </label>
        <select
          id="support-topic"
          value={formValue.topic}
          onChange={(event) => updateField("topic", event.target.value as SupportTopic)}
          onBlur={() => markTouched("topic")}
          className={cn(
            "mt-2 min-h-11 w-full rounded-xl border bg-[#fffaf1] px-3.5 text-sm text-[#243632] outline-none transition-colors",
            shouldShowError("topic") && errors.topic ? "border-[#cda98c]" : "border-[#d7cab1] focus:border-[#0f766e]"
          )}
        >
          {SUPPORT_TOPIC_VALUES.map((topic) => (
            <option key={topic} value={topic}>
              {copy.topicLabels[topic]}
            </option>
          ))}
        </select>
        {shouldShowError("topic") && errors.topic ? <p className="mt-1 text-xs text-[#8a5c47]">{errors.topic}</p> : null}
      </div>

      <div>
        <label htmlFor="support-subject" className="text-sm font-medium text-[#2d3f3a]">
          {copy.subjectLabel}
        </label>
        <input
          id="support-subject"
          type="text"
          value={formValue.subject}
          onChange={(event) => updateField("subject", event.target.value)}
          onBlur={() => markTouched("subject")}
          className={cn(
            "mt-2 min-h-11 w-full rounded-xl border bg-[#fffaf1] px-3.5 text-sm text-[#243632] outline-none transition-colors",
            shouldShowError("subject") && errors.subject ? "border-[#cda98c]" : "border-[#d7cab1] focus:border-[#0f766e]"
          )}
        />
        {shouldShowError("subject") && errors.subject ? <p className="mt-1 text-xs text-[#8a5c47]">{errors.subject}</p> : null}
      </div>

      <div>
        <label htmlFor="support-description" className="text-sm font-medium text-[#2d3f3a]">
          {copy.descriptionLabel}
        </label>
        <textarea
          id="support-description"
          value={formValue.description}
          onChange={(event) => updateField("description", event.target.value)}
          onBlur={() => markTouched("description")}
          rows={6}
          className={cn(
            "mt-2 w-full rounded-xl border bg-[#fffaf1] px-3.5 py-3 text-sm text-[#243632] outline-none transition-colors",
            shouldShowError("description") && errors.description
              ? "border-[#cda98c]"
              : "border-[#d7cab1] focus:border-[#0f766e]"
          )}
        />
        {shouldShowError("description") && errors.description ? (
          <p className="mt-1 text-xs text-[#8a5c47]">{errors.description}</p>
        ) : null}
      </div>

      <div>
        <label htmlFor="support-screenshot" className="text-sm font-medium text-[#2d3f3a]">
          {copy.screenshotLabel}
        </label>
        <input
          id="support-screenshot"
          type="file"
          accept={["image/png", "image/jpeg", "image/webp"].join(",")}
          onBlur={() => markTouched("screenshot")}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            updateField("screenshot", file);
            setTouched((current) => ({ ...current, screenshot: true }));
          }}
          className="mt-2 block w-full text-sm text-[#32453f] file:mr-3 file:rounded-lg file:border file:border-[#d7cab1] file:bg-[#fffaf1] file:px-3 file:py-2 file:text-sm file:text-[#32453f]"
        />
        <p className="mt-1 text-xs text-[#5f746d]">{copy.screenshotHint}</p>
        {shouldShowError("screenshot") && errors.screenshot ? (
          <p className="mt-1 text-xs text-[#8a5c47]">{errors.screenshot}</p>
        ) : null}
      </div>

      {submitError ? <p className="text-sm text-[#8a5c47]">{submitError}</p> : null}

      <button
        type="submit"
        disabled={isSubmitDisabled}
        className={cn(
          "inline-flex min-h-11 items-center justify-center rounded-xl border px-4 text-sm font-semibold transition-colors",
          isSubmitDisabled
            ? "cursor-not-allowed border-[#c8d4ce] bg-[#dfe7e4] text-[#6f807a]"
            : "border-[#0f766e]/30 bg-[#0f766e] text-white hover:bg-[#0d6860]"
        )}
      >
        {isSubmitting ? copy.submittingLabel : copy.submitLabel}
      </button>
    </form>
  );
}
